import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Send, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";

export default function CourseChat({ courseId, courseTitle }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(true);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const lastFetch = useRef(null);
  const listRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const params = lastFetch.current ? { since: lastFetch.current } : {};
      const { data } = await api.get(`/chat/${courseId}/messages`, { params });
      if (Array.isArray(data) && data.length > 0) {
        setMessages((prev) => [...prev, ...data]);
        lastFetch.current = data[data.length - 1].created_at;
      } else if (!lastFetch.current) {
        // first call with empty data, set a baseline timestamp
        lastFetch.current = new Date().toISOString();
      }
      setErr("");
    } catch (e) {
      if (e.response?.status === 403) {
        setErr("Enroll in this course to access the class chat.");
      } else if (e.response?.status !== 401) {
        setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    }
  };

  useEffect(() => {
    if (!courseId || !user) return;
    fetchMessages();
    const t = setInterval(fetchMessages, 3500);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [courseId, user?.id]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/chat/${courseId}/messages`, { body: body.trim() });
      setMessages((prev) => [...prev, data]);
      lastFetch.current = data.created_at;
      setBody("");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <section className="mt-12 border border-eduhu-border bg-eduhu-bg2" data-testid="course-chat">
      <header
        className="flex items-center justify-between px-5 py-4 border-b border-eduhu-border cursor-pointer hover:bg-eduhu-bg3/30 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-3">
          <MessageSquare size={18} className="text-eduhu-claret" />
          <div>
            <div className="overline text-eduhu-inkSoft">Class chat</div>
            <div className="font-serif text-lg text-eduhu-dark">{courseTitle || "Course"}</div>
          </div>
        </div>
        <button className="text-eduhu-inkSoft hover:text-eduhu-dark transition-colors" data-testid="chat-toggle">
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
      </header>

      {open && (
        <>
          <div ref={listRef} className="max-h-[420px] min-h-[260px] overflow-y-auto p-5 space-y-3" data-testid="chat-list">
            {err && <div className="text-sm text-eduhu-error border-l-2 border-eduhu-error pl-3">{err}</div>}
            {!err && messages.length === 0 && (
              <div className="text-sm text-eduhu-inkSoft text-center py-12">
                Be the first to say hello. Questions and discussion stay here for the whole cohort.
              </div>
            )}
            {messages.map((m) => {
              const mine = m.user_id === user.id;
              return (
                <div
                  key={m.id}
                  className={`flex ${mine ? "justify-end" : "justify-start"}`}
                  data-testid={`chat-msg-${m.id}`}
                >
                  <div className={`max-w-[80%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                    <div className="text-xs text-eduhu-inkSoft mb-1 flex items-center gap-2">
                      <span className={mine ? "" : "font-semibold text-eduhu-dark"}>
                        {mine ? "you" : m.name}
                      </span>
                      {m.role === "teacher" && (
                        <span className="text-[10px] px-1.5 py-0.5 border border-eduhu-gold text-eduhu-gold uppercase tracking-wider">teacher</span>
                      )}
                      <span>· {new Date(m.created_at).toLocaleTimeString()}</span>
                    </div>
                    <div
                      className={`px-4 py-2.5 leading-snug whitespace-pre-wrap break-words ${
                        mine
                          ? "bg-eduhu-dark text-eduhu-bg"
                          : m.role === "teacher"
                          ? "bg-eduhu-gold/15 border border-eduhu-gold/40"
                          : "bg-eduhu-bg3"
                      }`}
                    >
                      {m.body}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <form onSubmit={send} className="flex gap-2 p-3 border-t border-eduhu-border bg-eduhu-bg3/30">
            <input
              data-testid="chat-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Ask the cohort or your teacher…"
              className="flex-1 bg-eduhu-bg2 border border-eduhu-border px-3 py-2 text-sm focus:outline-none focus:border-eduhu-dark"
            />
            <button
              data-testid="chat-send"
              type="submit"
              disabled={busy || !body.trim()}
              className="eduhu-btn-primary !py-2 !px-3 text-sm"
            >
              <Send size={14} />
            </button>
          </form>
        </>
      )}
    </section>
  );
}
